import InitLoader from 'molecules/InitLoader';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { selectWorkspaceId } from 'store/userSlice';
import { fetchWorkspace, fetchWorkspaceVersion } from 'store/workspaceThunks';

import ExecutePage from 'containers/Execute';
import PlanPage from 'containers/Plan';
import ProjectModal from 'layout/organisms/ProjectModal/ProjectModal';
import { AppDispatch } from 'store';
import { LayoutState, selectLayout } from 'store/layoutSlice';
import { selectWorkspaceData } from 'store/workspaceSlice';

type WorkspaceState = 'EXECUTE' | 'PLAN';

const Workspace = (props: { workspaceState: WorkspaceState }) => {
    let navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const workspaceData = useSelector(selectWorkspaceData);
    const layoutState: LayoutState = useSelector(selectLayout);
    const workspaceId = useSelector(selectWorkspaceId);
    const workspaceState = props.workspaceState;
    const workspaceStatus = workspaceData.status.fetchWorkspace;
    const currentVersion = useRef<number | null>(null);

    const handleKeyDown = useCallback(
        (event: any) => {
            if (event.key === '1') {
                navigate('/plan');
            } else if (event.key === '2') {
                navigate('/execute');
            }
        },
        [navigate]
    );

    const checkWorkspaceVersion = useCallback(async () => {
        if (!workspaceId || workspaceStatus !== 'SUCCEEDED') return;
        
        try {
            const versionResponse = await dispatch(fetchWorkspaceVersion()).unwrap();
            const newVersion = versionResponse.version;
            const oldVersion = currentVersion.current;
            
            console.log('Version check - Current:', oldVersion, 'New:', newVersion);
            
            if (oldVersion !== null && oldVersion !== newVersion) {
                console.log('Version changed, refreshing workspace...');
                currentVersion.current = newVersion;
                await dispatch(fetchWorkspace(workspaceId));
            } else if (oldVersion === null) {
                currentVersion.current = newVersion;
            }
        } catch (error) {
            console.error('Failed to check workspace version:', error);
        }
    }, [dispatch, workspaceId, workspaceStatus]);

    const handleVisibilityChange = useCallback(() => {
        if (document.visibilityState === 'visible') {
            checkWorkspaceVersion();
        }
    }, [checkWorkspaceVersion]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    useEffect(() => {
        if (workspaceId) {
            dispatch(fetchWorkspace(workspaceId));
            dispatch(fetchWorkspaceVersion()).then((result) => {
                if (result.type === 'workspace/fetchWorkspaceVersion/fulfilled') {
                    currentVersion.current = (result.payload as any).version;
                }
            });
        }
    }, [dispatch, workspaceId]);

    useEffect(() => {
        layoutState.view === 'list'
            ? document.body.classList.remove('grid-view')
            : document.body.classList.add('grid-view');
    }, [layoutState.view]);

    useEffect(() => {
        layoutState.theme === 'light'
            ? document.body.classList.remove('dark-theme')
            : document.body.classList.add('dark-theme');
    }, [layoutState.theme]);

    useEffect(() => {
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [handleVisibilityChange]);

    if (workspaceStatus === 'IDLE' || workspaceStatus === 'LOADING') {
        return <InitLoader />;
    }

    if (workspaceStatus === 'FAILED') {
        return (
            <div style={{ margin: '32px' }}>
                <p>
                    Nieznany bląd aplikacji:{' '}
                    {workspaceData?.error.fetchWorkspace}
                </p>
            </div>
        );
    }

    if (workspaceStatus === 'SUCCEEDED') {
        return (
            <>
                {workspaceState === 'PLAN' && <PlanPage />}
                {workspaceState === 'EXECUTE' && <ExecutePage />}
                {layoutState.modal?.type === 'projectDetails' && (
                    <ProjectModal />
                )}
                {layoutState.modal?.type === 'archivedProjectDetails' && (
                    <ProjectModal archived />
                )}
            </>
        );
    }

    return <div>error</div>;
};

export default Workspace;
